import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { HttpModule } from '@angular/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { RichTextEditorAllModule } from '@syncfusion/ej2-angular-richtexteditor';
import { ImageCropperModule } from 'ngx-image-cropper';

// COMPONENTS
import { AppComponent } from './app.component';
import { FooterComponent } from './common/footer.component';
import { HeaderComponent } from './common/header.component';
import { HomePageComponent } from './home/home.component';
import { IndustriesPageComponent } from './industries/industries.component';
import { IndustryPageComponent } from './industries/industry.component';
import { CoursesPageComponent } from './industries/courses.component';
import { ProvidersPageComponent } from './industries/providers.component';
import { CourseProviderPageComponent } from './industries/course-provider.component';
import { AboutPageComponent } from './pages/about.component';
import { StudentsPageComponent } from './pages/students.component';
import { EmployersPageComponent } from './pages/employers.component';
import { ParentsPageComponent } from './pages/parents.component';
import { CareerAdvisorsPageComponent } from './pages/career-advisors.component';
import { LoginPageComponent } from './login/login.component';
import { VerifyAccountPageComponent } from './login/verify-account.component';
import { ForgotPasswordPageComponent } from './login/forgot-password.component';
import { ResetPasswordPageComponent } from './login/reset-password.component';
import { SignUpPageComponent } from './login/signup.component';
import { WelcomePageComponent } from './login/welcome.component';
import { ContactPageComponent } from './contact/contact.component';
import { TermsPageComponent } from './pages/terms.component';
import { PrivacyPageComponent } from './pages/privacy.component';
import { AdminComponent } from './admin/admin.component';
import { ModerateReviewsComponent } from './admin/moderate-reviews.component';
import { EditContentComponent } from './admin/edit-content.component';
import { SearchComponent } from './search/search.component';
import { FavouritesPageComponent } from "./user/favourites.component";
import { ProfilePageComponent } from "./user/profile.component";
import { UpdateDetailsComponent } from './user/update-details.component';
import { NotificationsComponent } from './user/notifications.component';
import { ReviewsComponent } from "./user/reviews.component";
import { PromotionStripComponent } from './admin/promotion-strip.component';
import { ExportCSVComponent } from './admin/exportcsv.component';
import { SearchUserComponent } from './admin/search-user.component';
import { CompetitionComponent } from './pages/competition.component';
import { BetaTestingProgramComponent } from './pages/beta-testing-program.component';


// MODALS
import { DynamicComponent } from './modals/dynamic.component';
import { LeaveReviewComponent } from './modals/leave-review.component';
import { PromotionComponent } from './promotion/promotion.component';

// SERVICE
import { HttpHelperService } from './services/helpers/http-helper.service';
import { ContactService } from './services/contact.service';
import { ReviewService } from './services/review.service';
import { IndustryService } from './services/industry.service';
import { UserService } from './services/user.service';
import { OverlayService } from "./services/overlay.service";
import { FavouriteService } from "./services/favourite.service";
import { SearchService } from "./services/search.service";
import { ReviewQuestions } from './services/review-questions.service';
import { EmailTaken } from './services/validators/email-taken.validator';
import { AuthGuard, AuthenticatedGuard, AdminGuard } from './services/guard/auth.guard';
import { NoSanitizePipe } from './services/pipes/no-sanitize.pipe';
import { PagerService } from './services/pagination.service';
import { PromotionService } from './services/promotion.service';
import { AdminService } from './services/admin.service';

// SHARED
import { PaginationComponent } from './shared/pagination/pagination.component';
import { SvgIconComponent } from './shared/svg-icon/svg-icon.component';

// THIRD PARTY
import { SelectModule } from 'ng2-select';

const routes = [
	{ path: '', component: HomePageComponent, data: { title: 'Get ABOVE | Do the course, rate the source', stage: true, footer: true, hasSearch: true } },

	{ path: 'login', component: LoginPageComponent, canActivate: [AuthenticatedGuard], data: { title: 'Login | Get ABOVE', stage: true, footer: false } },
	{ path: 'login/:id', component: LoginPageComponent, canActivate: [AuthenticatedGuard], data: { title: 'Login | Get ABOVE', stage: true, footer: false } },
	{ path: 'forgot-password', component: ForgotPasswordPageComponent, canActivate: [AuthenticatedGuard], data: { title: 'Forgot Password | Get ABOVE', stage: true, footer: false } },
	{ path: 'reset-password', component: ResetPasswordPageComponent, canActivate: [AuthenticatedGuard], data: { title: 'Reset Password | Get ABOVE', stage: true, footer: false } },
	{ path: 'reset-password/:token', component: ResetPasswordPageComponent, canActivate: [AuthenticatedGuard], data: { title: 'Reset Password | Get ABOVE', stage: true, footer: false } },
	{ path: 'verify-account', component: VerifyAccountPageComponent, canActivate: [AuthenticatedGuard], data: { title: 'Verify Account | Get ABOVE', stage: true, footer: false } },
	{ path: 'verify-account/:token', component: VerifyAccountPageComponent, canActivate: [AuthenticatedGuard], data: { title: 'Verify Account | Get ABOVE', stage: true, footer: false } },
	{ path: 'sign-up', component: SignUpPageComponent, canActivate: [AuthenticatedGuard], data: { title: 'Sign up | Get ABOVE', stage: true, footer: false } },
	{ path: 'welcome', component: WelcomePageComponent, canActivate: [AuthenticatedGuard], data: { title: 'Welcome | Get ABOVE', stage: true, footer: false } },

	{ path: 'about', component: AboutPageComponent, data: { title: 'About | Get ABOVE', stage: false, footer: true } },
	{ path: 'competition', component: CompetitionComponent, data: { title: 'Competition | Get ABOVE', stage: false, footer: true } },
	{ path: 'beta-testing-program', component: BetaTestingProgramComponent, data: { title: 'Beta Testing Program | Get ABOVE', noNav: true, stage: false, footer: true } },
	{ path: 'who-benefits/students', component: StudentsPageComponent, data: { title: 'Students | Who Benefits?', stage: false, footer: true } },
	{ path: 'who-benefits/employers', component: EmployersPageComponent, data: { title: 'Employers | Who Benefits?', stage: false, footer: true } },
	{ path: 'who-benefits/parents', component: ParentsPageComponent, data: { title: 'Parents | Who Benefits?', stage: false, footer: true } },
	{ path: 'who-benefits/career-advisors', component: CareerAdvisorsPageComponent, data: { title: 'Career Advisors | Who Benefits?', stage: false, footer: true } },
	{ path: 'terms-of-use', component: TermsPageComponent, data: { title: 'Terms of Use | Get ABOVE', stage: false, footer: true } },
	{ path: 'privacy', component: PrivacyPageComponent, data: { title: 'Privacy | Get ABOVE', stage: false, footer: true } },
	{ path: 'contact', component: ContactPageComponent, data: { title: 'Contact | Get ABOVE', stage: true, footer: true } },

	{ path: 'industries', component: IndustriesPageComponent, data: { title: 'Industries | Get ABOVE', stage: false, footer: true, hasSearch: true } },
	{ path: 'industry/:industry', component: IndustryPageComponent, data: { title: 'Industry | Get ABOVE', stage: true, footer: true } },
	{ path: 'industry/:industry/:career', component: CoursesPageComponent, data: { stage: false, footer: true } },
	{ path: 'industry/:industry/:career/:course', component: ProvidersPageComponent, data: { stage: true, footer: true } },
	{ path: 'industry/:industry/:career/:course/:provider', component: CourseProviderPageComponent, canActivate: [AuthGuard], data: { stage: false, footer: true } },
	{ path: 'course/:course/:provider', component: CourseProviderPageComponent, canActivate: [AuthGuard], data: { stage: false, footer: true } },
	{ path: 'industry', redirectTo: '/industries' },
	{ path: 'favourites', component: FavouritesPageComponent, canActivate: [AuthGuard], data: { title: 'My Favourites | Get ABOVE', stage: true, footer: true, footerAlt: true } },
	{ path: 'profile', component: ProfilePageComponent, canActivate: [AuthGuard], data: { title: 'My Profile | Get ABOVE', stage: true, footer: true, footerAlt: true, promoAlt: true } },
	{ path: 'profile/:page', component: ProfilePageComponent, canActivate: [AuthGuard], data: { title: false, stage: true, footer: true, footerAlt: true, promoAlt: true } },
	{ path: 'admin', component: AdminComponent, canActivate: [AuthGuard, AdminGuard], data: { title: 'Admin | Get ABOVE', stage: true, footer: true, footerAlt: true, promoAlt: true } },
	{ path: 'admin/:page', component: AdminComponent, canActivate: [AuthGuard, AdminGuard], data: { title: false, stage: true, footer: true, footerAlt: true, promoAlt: true } },
	{ path: 'search', component: SearchComponent, data: { title: 'Search | Get ABOVE', stage: true, footer: true, footerAlt: true } },

	{ path: '**', redirectTo: '' }
];

@NgModule({
	declarations: [
		AppComponent,
		FooterComponent,
		HeaderComponent,
		AboutPageComponent,
		StudentsPageComponent,
		EmployersPageComponent,
		ParentsPageComponent,
		CareerAdvisorsPageComponent,
		TermsPageComponent,
		PrivacyPageComponent,
		ContactPageComponent,
		CoursesPageComponent,
		DynamicComponent,
		SearchComponent,
		LeaveReviewComponent,
		FavouritesPageComponent,
		ProvidersPageComponent,
		ProfilePageComponent,
		CourseProviderPageComponent,
		HomePageComponent,
		IndustriesPageComponent,
		IndustryPageComponent,
		LoginPageComponent,
		ForgotPasswordPageComponent,
		NotificationsComponent,
		ResetPasswordPageComponent,
		ReviewsComponent,
		SignUpPageComponent,
		UpdateDetailsComponent,
		VerifyAccountPageComponent,
		WelcomePageComponent,
		NoSanitizePipe,
		AdminComponent,
		ModerateReviewsComponent,
		EditContentComponent,
		PaginationComponent,
		SvgIconComponent,
		PromotionComponent,
		PromotionStripComponent,
		ExportCSVComponent,
		SearchUserComponent,
		CompetitionComponent,
		BetaTestingProgramComponent
	],
	imports: [
		BrowserModule,
		BrowserAnimationsModule,
		FormsModule,
		HttpModule,
		NgbModule.forRoot(),
		ReactiveFormsModule,
		RouterModule,
		RouterModule.forRoot(routes),
		SelectModule,
		RichTextEditorAllModule,
		ImageCropperModule
	],
	providers: [
		AuthGuard,
		AdminGuard,
		AuthenticatedGuard,
		HttpHelperService,
		ContactService,
		ReviewService,
		IndustryService,
		UserService,
		OverlayService,
		FavouriteService,
		SearchService,
		ReviewQuestions,
		PagerService,
		EmailTaken,
		PromotionService,
		AdminService
	],
	bootstrap: [AppComponent]
})
export class AppModule { }



// WEBPACK FOOTER //
// ./src/app/app.module.ts